import type {StatusAbstraction} from '@gl/lit/common/constants/index.js';
import {
  abstractionToStatusesMap,
  AudienceStatus,
} from '@gl/lit/common/constants/index.js';
import type {
  Audience,
  AudienceIndex,
  AudienceTemplate,
} from '@gl/lit/types/audiences.js';
import {AudienceType} from '@gl/lit/types/audiences.js';
import type {Nullable} from '@gl/lit/types/general.js';
import type {Team} from '@gl/lit/types/teams.js';

export function isAudience(
  value?: Nullable<Audience | AudienceTemplate | AudienceIndex>
): value is Audience {
  return value?.audience_type === AudienceType.DEFAULT;
}

export function isAudienceTemplate(
  value?: Nullable<Audience | AudienceTemplate | AudienceIndex>
): value is AudienceTemplate {
  return value?.audience_type === AudienceType.TEMPLATE;
}

/**
 * Returns valid URL path, given an audience
 */
export function getAudiencePath(
  audience: Audience | AudienceTemplate | AudienceIndex
) {
  return getAudiencePathFromIds(audience.id, audience.dataset_group_id);
}

export function getAudiencePathFromIds(
  audienceId: number,
  datasetGroupId: number
) {
  return `/v2/dataset_groups/${datasetGroupId}/audiences/${audienceId}`;
}

export function getEmbedPath(
  audience: Audience | AudienceTemplate,
  orgId: string,
  teamId: string
) {
  const url = `/embed/audiences/${audience.id}/edit?organization_id=${orgId}&team_id=${teamId}`;
  return url;
}

export function getLegacyAudiencePath(audience: Audience | AudienceIndex) {
  return `/dataset_groups/${audience.dataset_group_id}/audiences/${audience.id}`;
}

export function copyAudiencePath(audience: Audience | AudienceTemplate) {
  return `/v2/dataset_groups/${audience.dataset_group_id}/audiences/new?audience_id=${audience.id}&copy=true`;
}

export function getArchiveAudiencePostPath(
  audience: Audience | AudienceTemplate
) {
  return `/v2/audiences/${audience.id}/archive`;
}

export function getUnarchiveAudiencePostPath(
  audience: Audience | AudienceTemplate
) {
  return `/v2/audiences/${audience.id}/unarchive`;
}

export function getArchiveAudiencePath() {
  return '/audiences';
}

export function getReviewAudiencePath(audience: Audience) {
  return `/dataset_groups/${audience.dataset_group_id}/audiences/${audience.id}`;
}

export function getReviewAudiencePostPath(audience: Audience) {
  return `/audiences/${audience.id}/review_audiences`;
}
/**
 * Returns all statuses that match a status "abstraction."
 * =======================================================
 * What is a status abstraction? Well, there are /two/ statuses that
 * technically mean "In-review", and likewise two status that mean "Approved."
 * A status abstraction is a single status that resolves into many.
 */
export function resolveStatusAbstraction(
  status: StatusAbstraction
): AudienceStatus[] | undefined {
  const statuses = abstractionToStatusesMap.get(status);
  return statuses;
}

export function isDraft(audience: Audience): boolean {
  return !!(audience.status === AudienceStatus.DRAFT);
}

export function isInReview(audience: Audience): boolean {
  return !![AudienceStatus.IN_REVIEW, AudienceStatus.PENDING_APPROVAL].includes(
    audience.status as AudienceStatus
  );
}

export function requiresApproval(audience: Audience): boolean {
  return !![
    AudienceStatus.DRAFT,
    AudienceStatus.IN_REVIEW,
    AudienceStatus.PENDING_APPROVAL,
  ].includes(audience.status as AudienceStatus);
}

/**
 * Determines whether or not the audience object passed from the server is an unsaved copy.
 */
export function isUnsavedCopy(
  audience: Partial<Audience | AudienceTemplate> | Audience | AudienceTemplate
): boolean {
  return !audience.id;
}

export function formatStatus(status: string) {
  // Replace anything but letters and numbers by space and capitalize each word
  return status
    .replace(/[^A-Z0-9]+/gi, ' ')
    .replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
}

export function audienceReviewIsEnabled(team: Team | undefined) {
  return team?.require_admin_approval;
}
